<template>
    <div
        class="ls-header__container"
        :class="{
            'show-deal-banner': showDealBanner,
            'show-address-banner': showAddressBanner,
            'show-both-banners': showDealBanner && showAddressBanner,
            'ls-cust-app-webview': hideNavigationHeader,
        }"
    >
        <header class="ls-header">
            <ls-deal-banner v-if="showDealBanner" />
            <div v-if="!hideNavigationHeader" class="grid grid--justify-center grid--bleed">
                <div class="grid__col-xs-12 grid__col-lg-11 grid__col-xlg-8 ls-header__col">
                    <div class="ls-header__content">
                        <span
                            v-ls-track.click="{ target: 'header-logo' }"
                            class="ls-header__logo"
                            :class="{
                                wordmark: !isLawnStarter,
                                lawnstarter: isLawnStarter,
                                lawnlove: isLawnLove,
                                homegnome: isHomeGnome,
                            }"
                        >
                            <img
                                v-if="mobile"
                                :src="isLawnStarter ? lsLogo : ''"
                                width="161"
                                height="31"
                                :title="`${brand} Home Screen`"
                                :alt="`${brand} Home Screen`"
                            />
                            <img
                                v-else
                                :src="isLawnStarter ? lsLogo : ''"
                                width="208"
                                height="40"
                                :title="`${brand} Home Screen`"
                                :alt="`${brand} Home Screen`"
                            />
                        </span>
                        <nav class="ls-header__links">
                            <a
                                v-if="!order.show_faq && !loading.quotingOrder"
                                v-ls-track.click="{ target: 'header-faq-link' }"
                                @click="openModal('faq')"
                                >FAQ</a
                            >
                            <a
                                v-ls-track.click="{ target: 'header-phone-number' }"
                                :href="`tel:${phoneRaw}`"
                            >
                                <i
                                    v-if="mobile"
                                    class="fa fa-fw fa-phone fa-lg ls-color--green"
                                ></i>
                                <span
                                    v-else
                                    :class="{
                                        'ls-color--green': isHomeGnome,
                                    }"
                                    >{{ phoneFormatted }}</span
                                >
                            </a>
                        </nav>
                    </div>
                </div>
            </div>
            <div v-if="showAddressBanner" class="ls-header__address-banner--container">
                <ls-mobile-address-banner />
            </div>
        </header>
    </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapGetters, mapActions } from 'vuex';
import {
    SCREEN_SIZE__GET_MOBILE_SCREEN,
    SCREEN_SIZE__GET_DESKTOP_SCREEN,
    MODAL__OPEN_MODAL,
    DEAL__UPDATE_BANNER,
} from '@/store/types';
import lsMobileAddressBanner from '@/components/original-mydot/mobile-address-banner/MobileAddressBanner.vue';
import { lsStorageService } from 'ls-vue-common';
import {
    generalStorageConstants,
    savedOrderModuleConstants,
} from '@/services/storage/storage-key.constants';
import { DealBannerConstants } from '@/constants';
import brandMixin from '@/mixins/brand.mixin';
import LsLogo from '@/assets/lawnstarter-logo.png';
import lsDealBanner from './components/DealBanner.vue';

export default {
    components: {
        lsMobileAddressBanner,
        lsDealBanner,
    },
    mixins: [brandMixin],
    data() {
        return {
            lsLogo: LsLogo,
        };
    },
    created() {
        let { deal, ls_db } = this.$route.params;

        // if ls_db exists as a qp, set to storage
        if (!_.isNil(ls_db)) {
            ls_db = _.toInteger(ls_db);
            lsStorageService.set(generalStorageConstants.LS_DB, ls_db);
        }

        // if ls_db is > 0 and qp deal is 'ctdnt' - show banner
        if (ls_db > 0 && deal === DealBannerConstants.v1) {
            this.updateBanner({ minutes: ls_db, version: 'v1' });
        } else {
            // otherwise, if (from storage) both ls_db > 0 and deal is 'ctdnt' - show banner
            ls_db = _.toInteger(lsStorageService.get(generalStorageConstants.LS_DB));
            deal = lsStorageService.get(savedOrderModuleConstants.DEAL);
            if (ls_db > 0 && deal === DealBannerConstants.v1) {
                this.updateBanner({ minutes: ls_db, version: 'v1' });
            }
        }
    },
    computed: {
        ...mapState(['deal', 'order', 'screenSize', 'loading']),
        ...mapGetters({
            mobile: SCREEN_SIZE__GET_MOBILE_SCREEN,
            desktop: SCREEN_SIZE__GET_DESKTOP_SCREEN,
        }),
        showAddressBanner() {
            return (
                !this.desktop &&
                !this.loading.quotingOrder &&
                (this.$route.meta.showOrderSummary || this.$route.meta.showAddress)
            );
        },
        showDealBanner() {
            return (
                !this.loading.quotingOrder &&
                this.$route.meta.showOrderSummary &&
                this.deal.banner.minutes &&
                this.deal.validDeal.deal_id
            );
        },
        hideNavigationHeader() {
            // if utm_source === 'customer-mobile', we are coming from RN WebView - hide nav
            return this.order.utm_source === 'customer-mobile';
        },
    },
    methods: {
        ...mapActions({
            openModal: MODAL__OPEN_MODAL,
            updateBanner: DEAL__UPDATE_BANNER,
        }),
    },
};
</script>

<style lang="scss" scoped>
.ls-header__container {
    height: rem(-1); // same as font-size of .ls-header__links
    padding: rem(2) 0; // same as padding-top/bottom of .ls-header__links

    @include max-screen(breakpoint(xs, max)) {
        padding: rem(-1); // same as padding-top/bottom of .ls-header__links
        margin-bottom: rem(0);
        &.show-address-banner {
            margin-bottom: 38px; // same height as order summary banner
        }
        &.show-deal-banner {
            margin-bottom: 30px; // same height as deal banner
        }
        &.show-both-banners {
            margin-bottom: 68px; // same height as order summary + deal banner
        }
    }
    @include min-screen(breakpoint(sm)) {
        padding: rem(2) rem(0); // same as padding-top/bottom of .ls-header__links
        margin-bottom: rem(0);
        &.show-address-banner {
            margin-bottom: 54px; // same height as order summary banner + rem(0) height
        }
        &.show-deal-banner {
            margin-bottom: 46px; // same height as deal banner + rem(0) height
        }
        &.show-both-banners {
            margin-bottom: 84px; // same height as deal banner + rem(0) height
        }
    }
    @include min-screen(breakpoint(md)) {
        margin-bottom: rem(0);
        &.show-deal-banner {
            margin-bottom: 46px; // same height as deal banner + rem(0) height
        }
    }
    &.ls-cust-app-webview {
        height: 0;
        margin-bottom: 0;
    }
}

.ls-header {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: z-index(header);

    border-bottom: 1px solid palette(gray, x-light);
    background-color: palette(white);
}

.ls-header__col {
    max-width: 1050px; // match max widths of signup form + order form

    @include min-screen(breakpoint(lg)) {
        max-width: 1200px;
    }
}

.ls-header__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ls-header__logo {
    cursor: default;
    padding-left: rem(0);
}

.ls-header__links {
    display: flex;
    align-items: stretch;

    > a:last-child {
        min-width: 61.83px;
    }

    > a {
        display: flex;
        justify-content: center;

        font-size: rem(-1); // same as height of .ls-header__container
        font-weight: 700;
        color: palette(green);
        line-height: 1; // prevent height of element from being modified

        &:hover {
            background-color: palette(green, xx-light);
        }

        @include max-screen(breakpoint(xs, max)) {
            border-left: 1px solid palette(gray, x-light);

            padding: rem(-1); // same as padding-top/bottom of .ls-header__container
        }
        @include min-screen(breakpoint(sm)) {
            padding: rem(2) rem(0); // same as padding-top/bottom of .ls-header__container
        }
    }
}

.ls-header__address-banner--container {
    padding: 3px rem(0);
    border-top: 1px solid palette(gray, x-light);
}

.themed-app {
    .wordmark {
        content: $theme-logo;
        &.lawnlove {
            height: 21px;
            margin: 10px 0 10px;
        }
    }
    .ls-header__address-banner--container {
        border-color: $theme-color-gray;
    }
}
</style>
